import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../../components/layout/Layout';
import Section from '../../../components/layout/Section';
import Container from '../../../components/layout/Container';

// UI Components
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import FeatureCard from '../../../components/ui/FeatureCard';
import { FAQList } from '../../../components/ui/FAQ';
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * Legal Industry Solutions Page
 */
const LegalIndustryPage: React.FC = () => {
  // Filter relevant case studies for the legal industry
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.industry === 'Legal'
  ).slice(0, 2);

  // Filter relevant FAQs
  const relevantFaqs = faqs.slice(0, 3);

  return (
    <Layout>
      <Hero
        title="AI Solutions for Legal Services"
        subtitle="Transform your legal practice with AI that enhances research, streamlines document review, and improves client services."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Key Challenges Section */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Industry Challenges We Solve</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <h3 className="text-xl font-semibold mb-3">Document Review</h3>
                <p className="text-neutral-700 mb-4">
                  Legal professionals spend countless hours reviewing documents. Our AI solutions dramatically
                  reduce review time by automatically identifying, categorizing, and extracting key information.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Legal Research</h3>
                <p className="text-neutral-700 mb-4">
                  Finding relevant cases, statutes, and precedents is time-consuming and complex. Our AI research
                  assistants quickly identify applicable legal information with greater accuracy and depth.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Contract Management</h3>
                <p className="text-neutral-700 mb-4">
                  Managing contracts across their lifecycle is challenging. Our solutions automate contract creation,
                  review, analysis, and management to reduce risk and improve efficiency.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Client Service</h3>
                <p className="text-neutral-700 mb-4">
                  Meeting client expectations for rapid, accurate service is increasingly difficult. Our AI tools help
                  legal teams deliver faster responses and more accurate billing while improving client satisfaction.
                </p>
              </Card>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Our Solutions Section */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Our Legal Solutions</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <FeatureCard
                title="AI-Powered Document Review"
                description="Machine learning systems that review contracts, pleadings, and discovery materials at speeds 60x faster than manual review with higher accuracy."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <FeatureCard
                title="Legal Research Assistant"
                description="AI tools that quickly identify relevant cases, statutes, and precedents, providing comprehensive legal research in a fraction of the time."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <FeatureCard
                title="Contract Management Automation"
                description="End-to-end contract lifecycle management with AI-powered creation, analysis, and tracking to reduce risk and improve compliance."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <FeatureCard
                title="Billing Optimization"
                description="AI systems that streamline time tracking, automatically generate accurate billing descriptions, and identify potentially billable activities."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <FeatureCard
                title="Case Outcome Prediction"
                description="Predictive analytics that evaluate potential case outcomes based on historical data, jurisdiction, judge assignments, and other relevant factors."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <FeatureCard
                title="Client Communication Tools"
                description="AI-powered communication systems that enhance client engagement, provide instant updates, and secure document sharing capabilities."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                }
              />
            </motion.div>
            </div>

            <div className="text-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => window.location.href = '/services'}
              >
                Explore All Our Services
              </Button>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Case Studies Section */}
      {relevantCaseStudies.length > 0 && (
        <Section bgColor="bg-white" padding="lg">
          <Container>
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Success Stories in Legal Services</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {relevantCaseStudies.map((study, index) => (
                  <motion.div
                    key={study.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex"
                  >
                    <Link to={study.url} className="w-full h-full">
                      <Card hover className="h-full flex flex-col">
                        <div className="aspect-video bg-neutral-200 rounded-t-lg overflow-hidden">
                          <img
                            src={study.imageSrc}
                            alt={study.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              if (img.nextElementSibling) {
                                (img.nextElementSibling as HTMLElement).style.display = 'flex';
                              }
                            }}
                          />
                          <div
                            className="w-full h-full bg-gradient-to-br from-primary to-secondary opacity-70 flex items-center justify-center hidden"
                            style={{ display: 'flex' }}
                          >
                            <span className="text-white text-lg font-semibold">{study.industry}</span>
                          </div>
                        </div>

                        <div className="p-6 md:p-8 flex-grow flex flex-col">
                          <div className="mb-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {study.solution}
                            </span>
                          </div>

                          <h3 className="text-xl font-semibold mb-3">{study.title}</h3>
                          <p className="text-neutral-600 mb-4 text-sm">{study.excerpt}</p>

                          <div className="mt-auto pt-2 border-t border-neutral-100">
                            <span className="inline-flex items-center text-primary font-medium text-sm">
                              Read full case study
                              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="text-center mt-10">
                <Link to="/case-studies" className="inline-flex items-center text-primary font-medium">
                  View all case studies
                  <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </Container>
        </Section>
      )}
      
      {/* FAQ Section */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQList faqs={relevantFaqs} />
            
            <div className="mt-10 text-center">
              <Link to="/contact" className="inline-flex items-center text-primary font-medium">
                Have more questions? Contact our legal AI specialists
                <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Call to Action */}
      <CallToAction
        title="Ready to transform your legal practice with AI?"
        description="Schedule a consultation to discuss how our AI solutions can help your legal team improve efficiency, accuracy, and client service."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default LegalIndustryPage;